import { Box, FormHelperText, TextareaAutosize, TextareaAutosizeProps, css, styled } from "@mui/material";
import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";

import { VALIDATE_DEBOUNCE_DELAY } from "@/modules/irohana-components/constants/common";
import { useFormSchema } from "@/modules/irohana-components/elements/Form";
import { useFormField } from "@/modules/irohana-components/elements/FormField/FormField";
import { useDebounce } from "@/modules/irohana-components/hooks/useDebounce";
import { useMount } from "@/modules/irohana-components/hooks/useMount";
import { background, blue, borderColor, divider, lightBlue, state, text } from "@/modules/irohana-components/theme/colors-new";
import { fonts, typography } from "@/modules/irohana-components/theme/typography";
import { rounded } from "@/modules/irohana-components/theme/variables";
import { convertFullWidthToHalfWidth, convertHalfWidthToFullWidth } from "@/modules/irohana-components/utils/pieces";

type TTextareaProps = {
  readonly?: boolean;
  previousValue?: string;
  error?: boolean;
  helperText?: string;
  nonControl?: boolean;
  disableFieldOnChange?: boolean;
  cleanWhitespace?: boolean;
} & Omit<TextareaAutosizeProps, "size">;

export const Textarea: FC<TTextareaProps> = ({
  readonly = false,
  previousValue,
  minRows = 2,
  maxRows = 5,
  error = false,
  placeholder = "入力してください",
  cleanWhitespace = true,
  nonControl = false,
  helperText,
  disableFieldOnChange = false,
  ...rest
}) => {
  const { field, fieldState } = useFormField(nonControl);
  const value = field ? field.value : rest.value;
  const isMounted = useMount();
  const fieldName = field?.name;
  const fieldsMeta = useFormSchema();
  const [innerValue, setInnerValue] = useState<string | null>(null);

  // Drop any pending local buffer when the field value changes from outside (e.g. form reset/setValue),
  // so a queued debounce commit cannot overwrite the externally-set value.
  useEffect(() => {
    setInnerValue(null);
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const input = e.target;
    let newValue = input.value;

    if (fieldName && fieldsMeta[fieldName]?.meta?.autoTransformToHalfWidth) {
      newValue = convertFullWidthToHalfWidth(newValue);
    }

    input.value = newValue;
    setInnerValue(newValue);
    rest.onChange?.(e);
  };

  useDebounce(
    () => {
      if (innerValue === null || innerValue === (value ?? "")) return;
      if (!disableFieldOnChange) field?.onChange?.(innerValue);
      setInnerValue(null);
    },
    [innerValue],
    VALIDATE_DEBOUNCE_DELAY,
  );

  const handleBlur: React.FocusEventHandler<HTMLTextAreaElement> = (e) => {
    const input = e.target;
    let newValue = input.value;

    if (fieldName && fieldsMeta[fieldName]?.meta?.autoTransformToFullWidth) {
      newValue = convertHalfWidthToFullWidth(newValue);
    }

    if (cleanWhitespace) {
      // Remove all tab characters
      newValue = newValue.replace(/\t/g, "");
      // Collapse multiple newline characters into a single newline
      newValue = newValue.replace(/\n+/g, "\n");
      // Collapse multiple half-width spaces into a single half-width space
      newValue = newValue.replace(/[ ]+/g, " ");
      // Collapse multiple full-width spaces into a single full-width space
      newValue = newValue.replace(/[　]+/g, "　");
      // Trim both half-width and full-width spaces from the start and end
      newValue = newValue.replace(/^[ \u3000]+|[ \u3000]+$/g, "").trim();
    }

    input.value = newValue;

    setInnerValue(null);
    if (!disableFieldOnChange) {
      field?.onChange?.(newValue);
    }
    field?.onBlur();
    rest.onBlur?.(e);
    rest.onChange?.(e);
  };

  const isPrevious = useMemo(() => {
    if (fieldState?.isDirty === false) return true;
    return Boolean(previousValue) && (field?.value ?? rest.value) == previousValue;
  }, [fieldState?.isDirty, previousValue, field?.value, rest.value]);

  const rawValue = innerValue ?? value;
  const inputValue = useMemo(() => {
    if (rawValue?.toString()) {
      return rawValue;
    }
    return "";
  }, [rawValue, isMounted]);

  return (
    <TextareaWrap>
      <StyledTextarea
        readonly={readonly}
        previous={isPrevious}
        minRows={minRows}
        maxRows={maxRows}
        error={error || Boolean(fieldState?.error)}
        {...rest}
        {...field}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {Boolean(helperText) && <FormHelperText>{helperText}</FormHelperText>}
    </TextareaWrap>
  );
};

const options = { shouldForwardProp: (propName: string) => !["textareaSize", "previous", "readonly", "error"].includes(propName) };

const TextareaWrap = styled(Box)`
  .MuiFormHelperText-root {
    margin-top: -4px;
  }
`;

/* Border-box height of N rows: N x line-height 21px (body14: 14px x 150%) + padding 2 x 5px + border 2 x 1px */
const rowsToHeight = (rows: string | number) => `calc(${Number(rows)} * 21px + 10px + 2px)`;

const StyledTextarea = styled(TextareaAutosize, options)<{ readonly: boolean; previous: boolean; error: boolean }>`
  width: 100%;
  resize: vertical;
  /* TextareaAutosize only sets an inline height, so bound the manual resize handle here */
  min-height: ${({ minRows = 2 }) => rowsToHeight(minRows)};
  max-height: ${({ maxRows = 5 }) => rowsToHeight(maxRows)};
  border: 1px solid ${divider.middle};
  border-radius: ${rounded.xs};
  padding: 5px 8px;
  color: ${text.primary};
  caret-color: ${blue[70]};
  ${css(typography.body14)};
  font-family: ${fonts};
  &::placeholder {
    color: ${text.tertiary};
  }

  &::placeholder {
    color: ${text.tertiary};
  }
  fieldset {
    border-color: ${divider.middle};
    border-radius: ${rounded.xs};
    border-width: 1px !important;
  }
  &:hover,
  &:focus {
    border-color: ${blue[70]};
    outline: none;
  }
  &.Mui-error {
    background: ${background.error};
    border-color: ${borderColor.error}!important;
  }
  &:disabled {
    background: ${background.disable};
    border-color: ${divider.middle}!important;
  }

  ${({ previous }) => previous && PreviousTextarea};
  ${({ readonly }) => readonly && readonlyTextarea};
  ${({ error }) => error && ErrorTextarea}
`;

const readonlyTextarea = css`
  background: ${background.disable};
  color: ${text.primary};
  border-color: ${divider.middle} !important;
  &::placeholder {
    opacity: 0;
  }
`;

const PreviousTextarea = css`
  color: ${lightBlue[60]};
`;

const ErrorTextarea = css`
  background: ${background.error};
  border-color: ${state.error_1}!important;
`;
